// ============================================================
// THE KILL-TEAM'S VERBS (Squad Mode stage 6) - what entering, the
// Challenge, leaving, starting, joining and stopping do, as plans the hook
// carries out: the pool's step (a compare-and-set, so it may be refused by
// the server's pool, not this screen's), each brother's new Squad Mode
// (as functions of his latest state), and the log's lines.
//
// When a step empties the pool, every brother drops to Solo Mode and
// nothing keeps running (`emptiedPlan`), in the same pass.
//
// Pure. Zero React dependency.
// ============================================================
import type { Combatant, JoinedAbility, KillTeamState } from '../roster/types';
import { isInTheFight } from '../roster/inTheFight';
import { cannotEnter, challengePasses, entered, turnStartOutcome, type DistanceTo } from './eligibility';
import { freshPool, join, refill, reset, spend, startAbility, stop, type Pool, type Running, type Step } from './pool';
import { leaderHere, type KillTeam } from './record';
import type { OathCopy } from './block';
import { resists } from './cohesionDamage';
import type { SquadAbility } from './tactics';

type NextState = (ks: KillTeamState | undefined) => KillTeamState | undefined;

export interface Act {
  /** The pool's step, against the server's pool; absent when the act leaves the pool alone. */
  pool?: (p: Pool) => Step;
  /** Instead of `pool`: a step that may find no pool yet and make one (the first fill of an Oath's pool). */
  opens?: (p: Pool | null) => Step;
  states: { id: string; next: NextState }[];
  lines: string[];
}
export type Planned = Act | { refused: string };

export interface Brothers {
  brother: Combatant;
  kt: KillTeam;
  pool: Pool | null;
  /** The brothers on his stage, still in the fight, him among them. */
  here: readonly Combatant[];
  /** Everyone at the table (whoever joined what he kept up, downed or not); `here` when absent. */
  roster?: readonly Combatant[];
  distance: DistanceTo;
}

const nameOf = (c: Combatant) => c.name || 'The brother';
const poolStep =
  (fn: (p: Pool) => Pool): ((p: Pool) => Step) =>
  (p) => ({ next: fn(p), lines: [] });

/** Enter with a Full Action; the first in opens the errata's free join, until his next turn start. */
export function enterPlan(b: Brothers): Planned {
  const why = cannotEnter(b);
  if (why) return { refused: why };
  const opener = b.pool?.entryWindow?.by;
  const free = opener && opener !== b.brother.id ? b.here.find((c) => c.id === opener) : undefined;
  return {
    pool: free ? undefined : poolStep((p) => ({ ...p, entryWindow: p.entryWindow ?? { by: b.brother.id } })),
    states: [{ id: b.brother.id, next: entered }],
    lines: [
      free
        ? `${nameOf(b.brother)} joins ${nameOf(free)} in Squad Mode (free).`
        : `${nameOf(b.brother)} enters Squad Mode (Full Action).`,
    ],
  };
}

/** The Cohesion Challenge (Free Action): `d10` at or under the pool enters; a failure follows the house rule. */
export function challengePlan(b: Brothers, d10: number, failureEnters: boolean): Planned {
  const why = cannotEnter(b);
  if (why) return { refused: why };
  const pool = b.pool!.pool;
  const said = `${nameOf(b.brother)}'s Cohesion Challenge: ${d10} against ${pool}`;
  if (challengePasses(d10, pool))
    return { states: [{ id: b.brother.id, next: entered }], lines: [`${said}, passed: he enters Squad Mode.`] };
  if (!failureEnters) return { states: [], lines: [`${said}, failed.`] };
  return {
    states: [{ id: b.brother.id, next: () => ({ pendingEntry: true }) }],
    lines: [`${said}, failed: he enters at his next turn, which it uses.`],
  };
}

/** Out of every running ability: the sustained ones he started end, he leaves the others' joiners. */
function withoutBrother(p: Pool, id: string): Pool {
  const running = p.running
    .filter((r) => !(r.sustained && r.startedBy === id))
    .map((r) => (r.joiners.includes(id) ? { ...r, joiners: r.joiners.filter((j) => j !== id) } : r));
  const { entryWindow, ...rest } = p;
  return { ...rest, running, ...(entryWindow && entryWindow.by !== id ? { entryWindow } : {}) };
}

/** Leave Squad Mode (free); `why` is the line's reason when it is not his choice (downed, out of range). */
export function leavePlan(brother: Combatant, pool: Pool | null, roster: readonly Combatant[], why?: string): Act {
  const ending = endingWith(pool, brother.id);
  return {
    pool: poolStep((p) => withoutBrother(p, brother.id)),
    states: [{ id: brother.id, next: () => undefined }, ...endJoined(roster, ending)],
    lines: [`${nameOf(brother)} ${why ?? 'leaves Squad Mode'}.`, ...ending.map((r) => `${r.name} ends with him.`)],
  };
}

/** Every brother's `joined` without these abilities. */
function endJoined(roster: readonly Combatant[], ending: readonly Running[]): { id: string; next: NextState }[] {
  const keys = new Set(ending.map((r) => r.key));
  return roster
    .filter((c) => c.state.killTeam?.joined?.some((j) => keys.has(j.key)))
    .map((c) => ({ id: c.id, next: (ks) => ks && withoutJoined(ks, keys) }));
}

function withoutJoined(ks: KillTeamState, keys: ReadonlySet<string>): KillTeamState {
  const joined = (ks.joined ?? []).filter((j) => !keys.has(j.key));
  const { joined: _was, ...rest } = ks;
  return joined.length ? { ...rest, joined } : rest;
}

const joinedOf = (r: Pick<Running, 'key' | 'abilityId' | 'name' | 'rev' | 'effects' | 'sustained'>): JoinedAbility => ({
  key: r.key,
  abilityId: r.abilityId,
  name: r.name,
  rev: r.rev,
  effects: r.effects,
  ...(r.sustained ? {} : { oneShot: true as const }),
});

const withJoined =
  (entry: JoinedAbility): NextState =>
  (ks) =>
    ks?.on ? { ...ks, joined: [...(ks.joined ?? []), entry], usedKey: entry.key } : ks;

/** The one-per-turn limit (a house rule): refused, unless the GM presses it anyway (logged as an override). */
function limitSays(brother: Combatant, limit: boolean, override: boolean): { refused: string } | { note: string } {
  if (!limit || !brother.state.killTeam?.usedKey) return { note: '' };
  if (override) return { note: ' (GM override)' };
  return { refused: `${nameOf(brother)} has had a squad ability since his last turn.` };
}

interface StartArgs {
  brother: Combatant;
  ability: SquadAbility;
  rev: number;
  sceneId: string | null;
  key: string;
  at: number;
  limit: boolean;
  override: boolean;
}

/** Start an Oath ability: its cost spent, its effects his at once. */
export function startPlan(a: StartArgs): Planned {
  if (!a.brother.state.killTeam?.on) return { refused: `${nameOf(a.brother)} is not in Squad Mode.` };
  const limit = limitSays(a.brother, a.limit, a.override);
  if ('refused' in limit) return limit;
  const entry = joinedOf({ ...a.ability, abilityId: a.ability.id, key: a.key, rev: a.rev });
  return {
    pool: (p) =>
      startAbility(p, {
        ability: a.ability,
        rev: a.rev,
        startedBy: a.brother.id,
        sceneId: a.sceneId,
        at: a.at,
        key: a.key,
      }),
    states: [{ id: a.brother.id, next: withJoined(entry) }],
    lines: [`${nameOf(a.brother)} starts ${a.ability.name}${limit.note}.`],
  };
}

/** Join a running ability: no action, no Cohesion. */
export function joinPlan(
  brother: Combatant,
  running: Running,
  sceneId: string | null,
  limit: boolean,
  override: boolean,
): Planned {
  if (!brother.state.killTeam?.on) return { refused: `${nameOf(brother)} is not in Squad Mode.` };
  const said = limitSays(brother, limit, override);
  if ('refused' in said) return said;
  return {
    pool: (p) => join(p, running.key, brother.id, sceneId),
    states: [{ id: brother.id, next: withJoined(joinedOf(running)) }],
    lines: [`${nameOf(brother)} joins ${running.name}${said.note}.`],
  };
}

/** Stop a running ability: gone from the pool and from every brother who had it. */
export function stopPlan(running: Running, roster: readonly Combatant[]): Act {
  return { pool: (p) => stop(p, running.key), states: endJoined(roster, [running]), lines: [] };
}

/**
 * A step left the pool at 0: every brother in Squad Mode (or waiting to enter) drops to Solo, nothing keeps
 * running. Null when the pool is not empty.
 */
export function emptiedPlan(
  next: Pool,
  roster: readonly Combatant[],
  tag: string,
  isOf: (c: Combatant, tag: string) => boolean,
): Act | null {
  if (next.pool > 0) return null;
  const inSquad = roster.filter((c) => c.state.killTeam && isOf(c, tag));
  if (!inSquad.length && !next.running.length) return null;
  return {
    pool: poolStep((p) => ({ pool: p.pool, running: [] })),
    states: inSquad.map((c) => ({ id: c.id, next: () => undefined })),
    lines: ['Cohesion is spent: every brother drops to Solo Mode.'],
  };
}

/** A cost the pool takes from outside (fire, Fear): what is left, never below 0, nothing at all once empty. */
const costStep =
  (n: number, why: string) =>
  (p: Pool): Step =>
    p.pool <= 0 ? { next: p, lines: [] } : spend(p, Math.min(n, p.pool), why);

/**
 * Heavy fire on a brother in Squad Mode (`cohesionCostOf` said 1): the (acting) leader on this stage resists with
 * his better of Command and Fellowship on `d100`; a pass holds the squad, a failure - or no leader answered here -
 * costs one.
 */
export function damagePlan(b: Brothers, sceneId: string | null, weapon: string, d100: number): Act {
  const leaderId = leaderHere(
    b.kt,
    sceneId,
    b.here.map((c) => c.id),
  );
  const leader = b.here.find((c) => c.id === leaderId);
  const cost = costStep(1, `${weapon} on ${nameOf(b.brother)}`);
  if (!leader) return { pool: cost, states: [], lines: ['No leader answers for the squad here: the cost lands.'] };
  const verdict = resists(leader.sheet, d100);
  return verdict.passed ? { states: [], lines: [verdict.line] } : { pool: cost, states: [], lines: [verdict.line] };
}

/** The (acting) leader failed his Fear test against a creature (D12): the pool pays its Fear Rating. */
export function fearPlan(leaderName: string, fearRating: number, creature: string): Act {
  return { pool: costStep(fearRating, `${leaderName} quails before ${creature}`), states: [], lines: [] };
}

/** At his turn start the pool forgets what was his until then: the free join he opened, the one-shots he started. */
function turnOver(p: Pool, id: string): Pool {
  const running = p.running.filter((r) => r.sustained || r.startedBy !== id);
  const { entryWindow, ...rest } = p;
  return { ...rest, running, ...(entryWindow && entryWindow.by !== id ? { entryWindow } : {}) };
}

const touchesPool = (p: Pool | null, id: string) =>
  !!p && (p.entryWindow?.by === id || p.running.some((r) => !r.sustained && r.startedBy === id));

/**
 * His turn starts (`supported`: another brother in support range on his stage now; `failureEnters`: the house rule
 * on a failed Challenge). Null when it changes nothing. Leaving Squad Mode here ends his sustained abilities too.
 */
export function turnStartPlan(b: Brothers, supported: boolean, failureEnters: boolean): Act | null {
  const ks = b.brother.state.killTeam;
  if (!ks && !touchesPool(b.pool, b.brother.id)) return null;
  const outcome = turnStartOutcome({ state: ks, name: nameOf(b.brother), supported, failureEnters });
  const leaves = !!ks?.on && !outcome.state;
  const id = b.brother.id;
  return {
    pool: turnPool(b.pool, id, leaves),
    states: [
      ...(ks ? [{ id, next: () => outcome.state }] : []),
      // The brothers who joined what he kept up lose it with him.
      ...(leaves
        ? endJoined(
            (b.roster ?? b.here).filter((c) => c.id !== id),
            endingWith(b.pool, id),
          )
        : []),
    ],
    lines: outcome.lines,
  };
}

/** The pool's part of his turn start; none when it touches nothing there (a pending entry alone is his state). */
function turnPool(pool: Pool | null, id: string, leaves: boolean): Act['pool'] {
  if (leaves) return poolStep((p) => withoutBrother(turnOver(p, id), id));
  return touchesPool(pool, id) ? poolStep((p) => turnOver(p, id)) : undefined;
}

/** The sustained abilities that end when this brother leaves Squad Mode. */
const endingWith = (p: Pool | null, id: string) => (p?.running ?? []).filter((r) => r.sustained && r.startedBy === id);

/**
 * What the GM's screen puts right on its own (useKillTeamReconcile): brothers in Squad Mode who are out of the fight,
 * and the tags whose pool READS 0 while a brother is still in Squad Mode. Never from a missing pool (gotcha 13).
 */
export function reconcileDue(
  roster: readonly Combatant[],
  pools: Record<string, Pool>,
  tagOf: (c: Combatant) => string | undefined,
  isOf: (c: Combatant, tag: string) => boolean,
): { down: string[]; strays: string[]; emptied: string[] } {
  const marked = roster.filter((c) => c.state.killTeam);
  // A STRAY is in Squad Mode (or waiting to enter) but no longer a brother of any kill-team: moved to another team,
  // or made someone's subordinate. Nothing else would ever take him out (the stage 6 review).
  const isStray = (c: Combatant) => {
    const tag = tagOf(c);
    return !tag || !isOf(c, tag);
  };
  const on = marked.filter((c) => !isStray(c) && c.state.killTeam?.on);
  const emptied = [...new Set(on.map(tagOf))].filter((t): t is string => !!t && pools[t]?.pool === 0);
  return {
    down: on.filter((c) => !isInTheFight(c)).map((c) => c.id),
    strays: marked.filter(isStray).map((c) => c.id),
    emptied,
  };
}

/** A stray's way out: his Squad Mode gone, nothing of the pool touched (it is not his kill-team's any more). */
export function strayPlan(c: Combatant): Act {
  return {
    states: [{ id: c.id, next: () => undefined }],
    lines: [`${nameOf(c)} is no longer a brother of a kill-team: back to Solo Mode.`],
  };
}

/**
 * The pool as the kill-team on the sheets wants it (plan modes-rework; the GM screen's reconcile): none yet - filled
 * at its start; filled for another Oath (or before Oaths were marked: old sessions are not carried over) - reset to
 * the new start, and every ability the new Oath does not unlock ends, on every brother who had it. Null when the pool
 * already is the kill-team's, it has no Oath to play, or no leader: his Fellowship is most of the start, and a pool
 * made without it would stay that low.
 */
export function oathPlan(kt: KillTeam, pool: Pool | null, roster: readonly Combatant[]): Act | null {
  const oath = kt.oath;
  if (!oath || !kt.leaderId || pool?.oathId === oath.id) return null;
  const unlocked = new Set(oath.abilities.map((a) => a.id));
  const ending = pool ? pool.running.filter((r) => !unlocked.has(r.abilityId)) : [];
  // What ends on the brothers is decided on THIS screen's pool, so the step holds only for that pool: one another
  // screen filled or reset meanwhile is left alone (the GM's screen plans again once it sees it).
  const opens = (p: Pool | null): Step => {
    if (oathKey(p) !== oathKey(pool)) return { refused: CHANGED };
    return p ? reset(p, kt.start.start, oath.id, unlocked) : fresh(kt.start.start, oath);
  };
  return { opens, states: endJoined(roster, ending), lines: [] };
}

const CHANGED = 'The Cohesion pool changed on another screen meanwhile: nothing was reset.';

/** Which pool a plan was made on: none (null), one marked with no Oath (''), or its Oath's id. */
const oathKey = (p: Pool | null): string | null => (p ? (p.oathId ?? '') : null);

const fresh = (start: number, oath: OathCopy): Step => ({
  next: freshPool(start, oath.id),
  lines: [`${oath.name}: Cohesion ${start}.`],
});

/** The GM's Refill: the pool back to the start the sheets work out now. Null with no leader (as `oathPlan`). */
export const refillPlan = (kt: KillTeam): Act | null =>
  kt.leaderId ? { pool: (p) => refill(p, kt.start.start), states: [], lines: [] } : null;
